/**
 * Assemblage universel des blocs (withStyle) + listes d'autorisation des
 * slots. Deplaces VERBATIM depuis config.tsx.
 */

import {
  containerBackgroundClass,
  normalizeColumnsCount,
  type LayoutBackground,
  type LayoutMaxWidth,
} from "@/lib/cms-puck/layout";
import {
  resolveColumnsBlockStyle,
  withoutTextAlign,
  type BlockStyle,
} from "@/lib/cms-puck/block-style";
import { withStyle } from "@/lib/cms-puck/with-style";
import { type RawComponentsConfig, type StyledComponentsConfig } from "./types";

/**
 * Widgets ATOMIQUES : le style cible l'élément lui-même (mode `"self"`), pas un
 * wrapper pleine largeur — leur render applique `useBlockStyleCss` sur leur
 * `<span>` (cf. Button/Icon). Les autres blocs gardent le wrapper section.
 */
const SELF_STYLED_BLOCKS = new Set<string>([
  "Button",
  "PaymentButton",
  "CatalogueCtaButton",
  "Icon",
]);

const FULL_WIDTH_LAYOUT_BLOCKS = new Set<string>([
  "Container",
  "Columns",
  "Grid",
  "Spacer",
]);

function layoutWrapperClassName(
  blockKey: string,
  props: Record<string, unknown>,
): string | undefined {
  if (!FULL_WIDTH_LAYOUT_BLOCKS.has(blockKey)) return undefined;
  const base = "w-full min-w-0";
  if (blockKey === "Container") {
    const bg = containerBackgroundClass(
      (props.background as LayoutBackground) ?? "none",
    );
    return [base, bg].filter(Boolean).join(" ");
  }
  return base;
}

/**
 * Style rendu dérivé du style stocké, par bloc (cf. WithStyleOptions.renderStyle) :
 * surcharges de colonnes des Colonnes, Bandeau centré par construction.
 */
const RENDER_STYLE: Record<
  string,
  (props: Record<string, unknown>) => BlockStyle | undefined
> = {
  Columns: (props) =>
    resolveColumnsBlockStyle(props.style as BlockStyle | undefined, {
      slots: normalizeColumnsCount(props.columns),
      stackOnMobile: Boolean(props.stackOnMobile),
    }),
  Banner: (props) => withoutTextAlign(props.style as BlockStyle | undefined),
};

/**
 * Applique `withStyle` à TOUS les blocs en une fois (le champ « style » et son
 * rendu sont ainsi ajoutés sans éditer chaque config individuellement).
 */
export function applyStyleToAll(raw: RawComponentsConfig): StyledComponentsConfig {
  const out = {} as Record<string, unknown>;
  for (const key of Object.keys(raw) as Array<keyof RawComponentsConfig>) {
    // withStyle opère sur un ComponentConfig générique ; on réaffirme le type
    // cible (parité 1:1 garantie par PuckComponents = RawPuckComponents + style).
    out[key as string] = withStyle(
      raw[key] as Parameters<typeof withStyle>[0],
      SELF_STYLED_BLOCKS.has(key as string) ? "self" : "wrapper",
      {
        wrapperClassName: (props) =>
          layoutWrapperClassName(key as string, props),
        contentMaxWidthFallback:
          key === "Container"
            ? (props) => (props.maxWidth as LayoutMaxWidth) ?? null
            : undefined,
        renderStyle: RENDER_STYLE[key as string],
      },
    );
  }
  return out as StyledComponentsConfig;
}

/** Composants autorisés dans les slots de mise en page (imbrication). */
export const NESTABLE_SLOT_ALLOW = [
  "Container",
  "Columns",
  "Grid",
  "Spacer",
  "TextImage",
  "Cta",
  "Banner",
  "Accordion",
  "Tabs",
  "ContactForm",
  // Formulaires membre : l'import pose ceux de l'ancien site dans la colonne
  // d'un Texte + image (connexion / inscription à côté de leur image).
  "MemberLogin",
  "MemberSignup",
  "Heading",
  "RichText",
  "Button",
  "PaymentButton",
  "CatalogueCtaButton",
  "Download",
  "Quote",
  "Icon",
  "Divider",
  "Media",
  "Gallery",
  "Carousel",
  "SocialLinks",
  "LogoGrid",
  "Testimonials",
  "MapEmbed",
  "VideoHero",
  "PdfReader",
  "Html",
  "News",
  "SelectionSections",
  "SelectionCategoryTitle",
  "SelectionCategoryDescription",
  "SelectionCategoryDetail",
  "ArtworkSelection",
  "ItemSpotlight",
  "CatalogueDecoupages",
  "CatalogueDecoupage",
  "CatalogueItemDetail",
  "Breadcrumb",
  "SiteLogo",
  "SiteMenu",
  "HeaderCta",
  "HeaderUserAuth",
  "HeaderBasket",
  "HeaderLanguage",
  "FooterContent",
] as const;

/** Blocs autorisés dans le slot `content` de Texte + Image (pas d'auto-imbrication). */
export const TEXT_IMAGE_SLOT_ALLOW = NESTABLE_SLOT_ALLOW.filter(
  (t) => t !== "TextImage",
);
